<template>
  <div>
    <breadcrumbs home="Sales representative"/>

    <v-card flat width="100%">
      <v-form v-model="valid" ref="form">
        <v-container class="px-6 pt-8 purtrak-scrollable" fluid>

          <v-row dense>
            <v-col cols="6" sm="3" lg="2">
              <v-checkbox label="Use account" v-model="useAccount" @change="salesRep.userId='';validate()" class="mt-1"/>
            </v-col>
            <v-col cols="12" sm="6" lg="4">
              <v-autocomplete label="Account" :items="users" item-text="name" item-value="userId" v-model="salesRep.userId" outlined dense
                              no-data-text="No account found"
                              :disabled="!useAccount" :rules="[accountSelected]" @change="changeAccount"
                              persistent-hint :hint="noPermission">
                <template v-slot:prepend-item>
                  <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="user={name:'',email:''};accountCreate=true">
                    <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add account
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-text-field label="Email" v-model="salesRep.email" :rules="[fieldRules.required,fieldRules.maxLength(50),fieldRules.email]" :counter="50" outlined dense
                            :disabled="useAccount"/>
            </v-col>
            <v-col cols="2" lg="1">
              <v-checkbox label="Active" v-model="salesRep.active" class="mt-1"/>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-text-field label="Name" v-model="salesRep.name" :rules="[fieldRules.required,fieldRules.maxLength(50)]" :counter="50" outlined dense/>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="6" sm="4" md="2">
              <decimal-number-input label="Commission" v-model="salesRep.commissionPercent" prefix="" suffix="%" :precision="2"/>
            </v-col>
          </v-row>

          <v-row dense v-if="useAccount">
            <v-col cols="12" sm="6" lg="4">
              <v-checkbox label="Restrict view to rep's customers only" v-model="salesRep.restricted" class="mt-1"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="pr-6">
        <v-spacer/>
        <v-btn plain color="primary" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="accountCreate" persistent max-width="450">
      <v-card>
        <v-card-title>Create account</v-card-title>
        <v-divider/>
          <v-form v-model="accountCreateValid" immediate ref="accountCreateform">
            <v-container class="mt-2">
              <v-row class="mb-2">
                <v-col>
                  The new sales representative will be given sales write and inventory read access by default.
                  You can edit their access rights under Settings -> Users.
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field label="Name" v-model="user.name" :rules="[fieldRules.required,fieldRules.maxLength(50)]" :counter="50" outlined dense/>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field label="Email" v-model="user.email" :rules="[fieldRules.required,fieldRules.maxLength(50),fieldRules.email]" :counter="50" outlined dense
                                hint="If user doesn't exist, they will be invited to join PŪRTRAK" persistent-hint/>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-spacer></v-spacer>
          <v-btn plain color="primary" @click="accountCreate=false">Cancel</v-btn>
          <v-btn color="primary" @click="createAccount()" :loading="saving">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-leave v-model="next"/>
  </div>
</template>

<script>
import confirmLeave from '@/mixins/confirmLeave'

export default {
  name: 'SalesSalesRepsEdit',
  mixins: [confirmLeave],
  props: {
    salesRepId: {type:String, required:false}
  },
  data() {
    return {
      salesRep: {userId:'', name:'', email:'', active:true, commissionPercent:0, restricted:false},
      useAccount: false,
      users: [],
      valid: true,
      dirty: false,
      saving: false,
      accountSelected: v => !this.useAccount || !!v || 'Account required',
      accountCreate: false,
      user: {},
      accountCreateValid: true
    }
  },
  computed: {
    noPermission() {
      const account = this.users.find(account => account.userId === this.salesRep.userId);
      if (account && !account.permissions.includes('selling.write'))
        return 'User permissions do not allowing selling';
    }
  },
  watch: {
    salesRep: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },

  async created() {
    const results = await this.$apiCall(`query SalesSalesRepsEdit($id:ID!, $edit:Boolean!) {
                                            company {
                                              users {
                                                userId,name,email,permissions
                                              }
                                            }
                                            salesRep(id:$id) @include(if:$edit) {
                                              id,userId,name,email,active
                                              commissionPercent,restricted
                                            }
                                          }`,
                                        {id:this.salesRepId??'',edit:!!this.salesRepId});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.users = results.data.company.users;
    if (this.salesRepId) {
      this.salesRep = results.data.salesRep;
      this.useAccount = !!this.salesRep.userId;
    }
    this.$nextTick(() => this.dirty = false);
  },
  mounted() {
    this.validate();
  },
  methods: {
    validate() {
      this.$refs.form.validate();
    },
    changeAccount() {
      if (this.useAccount) {
        const account = this.users.find(account => account.userId === this.salesRep.userId);
        this.salesRep.name = account?.name ?? 'Not found';
        this.salesRep.email = account?.email ?? 'Not found';
      }
    },
    async createAccount() {
      if (!this.$refs.accountCreateform.validate())
        return;

      this.saving = true;
      this.user.permissions = 'inventory.read selling.write selling.read';
      const results = await this.$apiCall(`mutation SalesSalesRepsEdit($user: CompanyUserCreateInput!) {
                                              companyUserCreate(input:$user) {userId,name,email,permissions}
                                            }`,
                                            {user:this.user});
      this.saving = false;
      if (!results.error) {
        this.users.push(results.data.companyUserCreate);
        this.salesRep.userId = results.data.companyUserCreate.userId;
        this.changeAccount();
        this.accountCreate = false;
      }
    },
    cancel() {
      this.$router.back();
    },
    async save() {
      if (!this.$refs.form.validate()) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      const results = await this.$apiCall(`mutation SalesSalesRepsEdit($input: SalesRepInput!) {
                                             salesRepCrupdate(input:$input) {id}
                                           }`,
                                          {input:this.salesRep})
      if (results.error)
        return;

      this.$nextTick(() => {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.salesRepId?'Updated':'Created'} sales representative ${this.salesRep.name}`});
      });
    }
  }
}
</script>